import { Injectable } from '@angular/core';
import { ApiPartnerMembershipSubscriptionService } from '../services';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { GetPartnerMembershipSubscriptionsByPartnerId, GetPartnerActiveMembershipSubscriptionsByPartnerId } from '../actions';
import { PartnerMembershipSubscription } from '../models';
import { produce } from 'immer';

export interface PartnerMembershipSubscriptionStateModel {
    subscription: PartnerMembershipSubscription | null;
    hasActivePartnerSubscription: boolean | null;
    loaded: boolean;
    subscriptions: PartnerMembershipSubscription[] | null;
}

export const partnerMembershipStateDefaults: PartnerMembershipSubscriptionStateModel = {
    subscription: null,
    hasActivePartnerSubscription: null,
    loaded: false,
    subscriptions: null
};

@State<PartnerMembershipSubscriptionStateModel>({
    name: 'partnerMembershipSubscription',
    defaults: partnerMembershipStateDefaults
})

@Injectable()
export class PartnerMembershipSubscriptionState {

    @Selector()
    static subscription(state: PartnerMembershipSubscriptionStateModel): PartnerMembershipSubscription {
        return state.subscription!;
    }

    @Selector()
    static subscriptions(state: PartnerMembershipSubscriptionStateModel): PartnerMembershipSubscription[] {
        return state.subscriptions!;
    }

    @Selector()
    static hasActivePartnerSubscription(state: PartnerMembershipSubscriptionStateModel): boolean {
        return state.hasActivePartnerSubscription!;
    }

    @Selector()
    static loaded(state: PartnerMembershipSubscriptionStateModel): boolean {
        return state.loaded;
    }

    constructor(private partnerMembershipSubscriptionService: ApiPartnerMembershipSubscriptionService) {
    }

    @Action(GetPartnerMembershipSubscriptionsByPartnerId)
    async getPartnerMembershipSubscriptionsById(ctx: StateContext<PartnerMembershipSubscriptionStateModel>, action: GetPartnerMembershipSubscriptionsByPartnerId): Promise<void> {
        const membershipSubscription = await this.partnerMembershipSubscriptionService.getPartnerMembershipSubscriptionsById(action.partnerId).toPromise() as any;

        const activeSubscription = membershipSubscription.find((subscription: any) => subscription.isActive);
        ctx.patchState({
            hasActivePartnerSubscription: activeSubscription != null,
            loaded: true
        });

        if (activeSubscription != null) {
            ctx.patchState({
                subscription: {
                    id: activeSubscription.id,
                    title: activeSubscription.title,
                    description: activeSubscription.description,
                    partnerId: activeSubscription.partnerId,
                    price: activeSubscription.price,
                    currency: activeSubscription.currency,
                    isActive: activeSubscription.isActive,
                    doesExpire: activeSubscription.doesExpire,
                    isTicketBased: activeSubscription.isTicketBased,
                    isTimeBased: activeSubscription.isTimeBased,
                    numberToAdd: activeSubscription.numberToAdd,
                    timePeriod: activeSubscription.timePeriod,
                    totalTicket: activeSubscription.totalTicket,
                    startDate: activeSubscription.startDate,
                    isAutoApprove: activeSubscription.isAutoApprove,
                    externalFormUrl: activeSubscription.externalFormUrl,
                    isRecurring: activeSubscription.isRecurring,
                    isDisplayed: activeSubscription.isDisplayed
                },
            });
        } else {
            ctx.patchState({
                subscription: null
            });
        }
    }

    @Action(GetPartnerActiveMembershipSubscriptionsByPartnerId)
    async getPartnerActiveMembershipSubscriptionsById(ctx: StateContext<PartnerMembershipSubscriptionStateModel>, action: GetPartnerMembershipSubscriptionsByPartnerId): Promise<void> {
        const state = ctx.getState();
        const membershipSubscriptions = await this.partnerMembershipSubscriptionService.getPartnerActiveMembershipSubscriptionsById(action.partnerId).toPromise() as any;

        if (membershipSubscriptions.length > 0) {
            const value = produce(state, draft => { 
                draft.subscriptions = membershipSubscriptions,
                draft.hasActivePartnerSubscription = (membershipSubscriptions.length > 0),
                draft.loaded = true
            });
            ctx.setState(value);
        } else {
            ctx.patchState({
                hasActivePartnerSubscription: false,
                subscription: null,
                loaded: true,
                subscriptions: []
            });
        }
    }
}
